import React from "react";
import styled from "styled-components";
import { Table as BaseTable } from "@cinesys/ui/components";
import {
  ProfileStatus,
  STATUS_LABEL,
  Strategy,
  STRATEGY_LABEL,
  WorkflowMode,
  WORKFLOW_LABEL,
} from "./config";

/**
 * Shared styled primitives for the `metadata-import` interface — the generic building blocks
 * (page shell, cards, section labels, status/workflow/strategy pills, stat tiles) reused across
 * the Overview and Profile screens. Mirrors the shape of `spotter/*` screens (a `Page` +
 * `SpotterHeader` + a `Wrap` that re-aliases `@cinesys/ui`'s generic tokens to the interface
 * palette) but on the `--mi*` tokens from `MetadataImportThemeProvider`.
 */

export const Page = styled.div`
  min-height: 100%;
  color: var(--miInk);
  font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
  font-size: 15px;
  line-height: 1.5;
`;

/** Re-alias `@cinesys/ui`'s generic tokens to this interface's palette (customer-frontend
 *  CLAUDE.md convention) so shared controls (`Table`, `Input`, `Select`, `Button`) pick up the
 *  Metadata Import look. Copied from the Spotter screens' `Wrap`, retargeted to `--mi*`. */
export const Wrap = styled.div`
  padding: 22px 22px 56px;
  max-width: 1060px;
  margin: 0 auto;

  --dark400: var(--miRaised);
  --dark500: var(--miLine2);
  --dark600: var(--miLine2);
  --dark700: var(--miInkFaint);
  --gray400: var(--miInk);
  --gray500: var(--miInk);
  --blue300: var(--miAccentBright);
  --blue400: var(--miAccent);
  --blue500: var(--miAccent);
`;

export const PageHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 14px;
  margin: 4px 0 4px;
  flex-wrap: wrap;
`;

export const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  margin: 0;
`;

export const SubNote = styled.p`
  color: var(--miInkFaint);
  font-size: 13px;
  margin: 4px 0 22px;
  max-width: 82ch;
`;

export const Card = styled.div`
  background: var(--miSurface);
  border: 1px solid var(--miLine);
  border-radius: 14px;
  box-shadow: var(--miShadow);
`;

export const Panel = Card;

export const PanelHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid var(--miLine);
`;

export const SectionLabel = styled.span`
  font-family: ui-monospace, Menlo, monospace;
  font-size: 12px;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  color: var(--miInkFaint);
`;

/** Quiet sub-group label INSIDE a section card — a titled divider that replaces a nested bordered
 *  box. Same restrained treatment Spotter uses (OPTIONS / FILE NAME TEMPLATES): uppercase, tracked,
 *  faint, sitting on a hairline. Never bold-white (that competes with the section title). */
export const SubLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 26px 0 14px;
  padding-bottom: 9px;
  border-bottom: 1px solid var(--miLine);
  font-family: ui-monospace, Menlo, monospace;
  font-size: 11.5px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--miInkFaint);

  &:first-child {
    margin-top: 2px;
  }
`;

/** A right-rail panel (CHANGES / PREVIEW / RUN HISTORY). Quiet titled header on a hairline, then a
 *  body — the sibling of a Section card but sized for the secondary column. */
export const SidePanel = styled.section`
  background: var(--miSurface);
  border: 1px solid var(--miLine);
  border-radius: 12px;
  overflow: hidden;

  & + & {
    margin-top: 12px;
  }

  > header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 11px 14px;
    border-bottom: 1px solid var(--miLine);
    font-family: ui-monospace, Menlo, monospace;
    font-size: 11px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--miInkFaint);
  }

  > .body {
    padding: 12px 14px;
  }
`;

/** Small tabular counter shown next to a SidePanel/section title (e.g. "3 / 3"). */
export const Count = styled.span`
  font-family: ui-monospace, Menlo, monospace;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.04em;
  color: var(--miInkSoft);
`;

export const Mono = styled.span`
  font-family: ui-monospace, Menlo, monospace;
  font-variant-numeric: tabular-nums;
  color: var(--miInkSoft);
  font-size: 12.5px;
`;

/** Border-below table header instead of the base `Table`'s filled-background stripe + drop-shadow
 *  — same treatment the Spotter screens apply (see `UsageScreen`'s `Table`). */
export const Table = styled(BaseTable)`
  > header {
    background-color: transparent;
    border-bottom: 1px solid var(--miLine);
    box-shadow: none;
  }
` as typeof BaseTable;

/* ----------------------------------------------------------------- pills */

const PillBase = styled.span<{ $fg: string; $bg: string }>`
  display: inline-flex;
  /* Hug content when used as a Table columnGrid cell — grid items default to
   * justify-self: stretch, which would blow a pill out to the full column width. */
  justify-self: start;
  align-items: center;
  gap: 6px;
  font-family: ui-monospace, Menlo, monospace;
  font-size: 11px;
  letter-spacing: 0.04em;
  padding: 2px 9px;
  border-radius: 6px;
  white-space: nowrap;
  color: ${(p) => p.$fg};
  background: ${(p) => p.$bg};
  border: 1px solid color-mix(in srgb, ${(p) => p.$fg} 42%, transparent);
`;

const Dot = styled.i<{ $fg: string }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${(p) => p.$fg};
`;

const STATUS_TONE: Record<ProfileStatus, { fg: string; bg: string }> = {
  active: { fg: "var(--miOk)", bg: "var(--miOkSoft)" },
  draft: { fg: "var(--miWarn)", bg: "var(--miWarnSoft)" },
  paused: { fg: "var(--miMuted)", bg: "var(--miMutedSoft)" },
  error: { fg: "var(--miDanger)", bg: "var(--miDangerSoft)" },
};

export function StatusPill({ status }: { status: ProfileStatus }) {
  const t = STATUS_TONE[status];
  return (
    <PillBase $fg={t.fg} $bg={t.bg}>
      <Dot $fg={t.fg} />
      {STATUS_LABEL[status]}
    </PillBase>
  );
}

const WORKFLOW_TONE: Record<WorkflowMode, { fg: string; bg: string }> = {
  "collection-sync": { fg: "var(--miSync)", bg: "var(--miSyncSoft)" },
  "asset-import": { fg: "var(--miImport)", bg: "var(--miImportSoft)" },
};

export function WorkflowBadge({ mode }: { mode: WorkflowMode }) {
  const t = WORKFLOW_TONE[mode];
  return (
    <PillBase $fg={t.fg} $bg={t.bg}>
      {WORKFLOW_LABEL[mode]}
    </PillBase>
  );
}

const STRATEGY_TONE: Record<Strategy, { fg: string; bg: string }> = {
  append: { fg: "var(--miAppend)", bg: "var(--miAppendSoft)" },
  overwrite: { fg: "var(--miOverwrite)", bg: "var(--miOverwriteSoft)" },
  rewrite: { fg: "var(--miRewrite)", bg: "var(--miRewriteSoft)" },
};

export function StrategyPill({ strategy }: { strategy: Strategy }) {
  const t = STRATEGY_TONE[strategy];
  return (
    <PillBase $fg={t.fg} $bg={t.bg}>
      {STRATEGY_LABEL[strategy]}
    </PillBase>
  );
}

/** Neutral labelled pill (format, cadence, generic chips). */
export const Chip = styled.span`
  display: inline-flex;
  justify-self: start;
  align-items: center;
  gap: 6px;
  font-family: ui-monospace, Menlo, monospace;
  font-size: 11px;
  letter-spacing: 0.04em;
  padding: 2px 9px;
  border-radius: 6px;
  white-space: nowrap;
  color: var(--miInkSoft);
  background: var(--miRaised);
  border: 1px solid var(--miLine);
`;

/* ----------------------------------------------------------------- stat tiles */

export const StatRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 12px;
`;

const StatTile = styled.div<{ $tone?: string }>`
  background: var(--miSurface);
  border: 1px solid var(--miLine);
  border-radius: 12px;
  padding: 14px 16px;

  .v {
    font-size: 26px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
    color: ${(p) => p.$tone ?? "var(--miInk)"};
  }
  .l {
    margin-top: 7px;
    font-size: 11px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--miInkFaint);
    font-family: ui-monospace, Menlo, monospace;
  }
`;

export function Stat({
  value,
  label,
  tone,
}: {
  value: React.ReactNode;
  label: string;
  tone?: string;
}) {
  return (
    <StatTile $tone={tone}>
      <div className="v">{value}</div>
      <div className="l">{label}</div>
    </StatTile>
  );
}

/* ----------------------------------------------------------------- key/value rows */

/* ----------------------------------------------------------------- option cards + toggle */

/** Selectable option card (workflow / schedule mode). In this spike selection is visual only —
 *  it reflects the profile's configured value and can be highlighted, but does not persist. */
export const OptionCard = styled.button<{ $selected: boolean }>`
  appearance: none;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: var(--miInkSoft);
  background: ${(p) =>
    p.$selected ? "var(--miAccentSoft)" : "var(--miSurface)"};
  border: 1.5px solid
    ${(p) => (p.$selected ? "var(--miAccent)" : "var(--miLine)")};
  border-radius: 12px;
  padding: 14px 16px;
  display: block;
  transition: border-color 0.12s ease, background 0.12s ease;

  &:hover {
    border-color: ${(p) =>
      p.$selected ? "var(--miAccent)" : "var(--miLine2)"};
  }

  .ttl {
    display: flex;
    align-items: center;
    gap: 9px;
    color: #fff;
    font-weight: 600;
    font-size: 15px;
  }
  .sub {
    margin-top: 6px;
    font-size: 12.5px;
    color: var(--miInkFaint);
    line-height: 1.45;
  }
`;

/** Read-only on/off indicator (e.g. "Include descendants"). */
export function Toggle({ on }: { on: boolean }) {
  return <TogglePill $on={on}>{on ? "On" : "Off"}</TogglePill>;
}

const TogglePill = styled.span<{ $on: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-family: ui-monospace, Menlo, monospace;
  font-size: 11px;
  padding: 3px 10px 3px 8px;
  border-radius: 999px;
  color: ${(p) => (p.$on ? "var(--miOk)" : "var(--miInkFaint)")};
  background: ${(p) => (p.$on ? "var(--miOkSoft)" : "var(--miMutedSoft)")};
  border: 1px solid
    ${(p) =>
      p.$on
        ? "color-mix(in srgb, var(--miOk) 42%, transparent)"
        : "var(--miLine)"};

  &::before {
    content: "";
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${(p) => (p.$on ? "var(--miOk)" : "var(--miInkFaint)")};
  }
`;

export const KeyGrid = styled.dl`
  display: grid;
  grid-template-columns: 190px 1fr;
  gap: 12px 20px;
  margin: 0;

  dt {
    font-size: 12px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--miInkFaint);
    font-family: ui-monospace, Menlo, monospace;
    align-self: center;
  }
  dd {
    margin: 0;
    color: var(--miInkSoft);
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 3px 0;
    dd {
      margin-bottom: 12px;
    }
  }
`;
